import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO, { generateServiceSchema, generateBreadcrumbSchema } from '@/components/SEO';
import { getServiceBySlug, SERVICES } from '@/lib/services';
import { ArrowUpRight, CheckCircle, ArrowRight, ArrowLeft, Sparkles, Zap } from 'lucide-react';
import { trackServiceView, trackBookCall, trackCTA } from '@/lib/gtm';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => { if (service) trackServiceView(service.slug); }, [service]);

  if (!service) return <Navigate to="/services" replace />;

  const otherServices = SERVICES.filter((s) => s.slug !== slug);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      generateBreadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: service.title, path: `/services/${service.slug}` },
      ]),
      generateServiceSchema({ name: service.title, description: service.short }),
    ],
  };

  const Icon = service.icon;

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid={`service-detail-${service.slug}`}>
      <SEO
        title={`${service.title} — ${service.tagline}`}
        description={service.short}
        keywords={service.keywords}
        url={`/services/${service.slug}`}
        image={service.og_image}
        structuredData={structuredData}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial opacity-80" />
        <div className="absolute top-40 right-10 w-96 h-96 rounded-full blur-3xl opacity-30" style={{ background: 'radial-gradient(circle, #ff5a1f, transparent)' }} />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Link to="/services" className="inline-flex items-center gap-2 text-[#5B5B5B] hover:text-[#ff5a1f] text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 rounded-2xl bg-[#ff5a1f] flex items-center justify-center">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <div className="eyebrow">{service.hero.eyebrow}</div>
              </div>
              <h1 className="font-syne text-4xl sm:text-5xl lg:text-7xl font-bold text-[#0A0A0A] mb-8 tracking-tight leading-[0.95]">
                {service.hero.heading.split('.').map((part, i, arr) => (
                  <span key={i}>
                    {i === arr.length - 2 ? <span className="font-editorial text-[#ff5a1f]">{part}.</span> : `${part}${i < arr.length - 1 ? '.' : ''}`}
                    {i < arr.length - 2 && <br />}
                  </span>
                ))}
              </h1>
              <p className="text-[#3A3A3A] text-lg lg:text-xl max-w-2xl mb-10 leading-relaxed">{service.hero.sub}</p>

              <div className="flex flex-wrap gap-4">
                <Link to="/book-appointment" data-testid="service-cta-primary" onClick={() => trackBookCall(`service_${service.slug}_hero`)}
                  className="bg-[#0A0A0A] text-white px-7 py-4 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press inline-flex items-center gap-2">
                  Book a strategy call <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" data-testid="service-cta-secondary" onClick={() => trackCTA('service_get_quote', `service_${service.slug}`)}
                  className="border-2 border-[#0A0A0A] text-[#0A0A0A] px-7 py-4 text-sm font-semibold rounded-full hover:bg-[#0A0A0A] hover:text-white transition-colors inline-flex items-center gap-2">
                  Get a custom quote
                </Link>
              </div>
            </div>

            {/* Metrics card */}
            <div className="lg:col-span-4">
              <div className="dark-section text-white rounded-3xl p-8 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-40" style={{ background: 'radial-gradient(circle, #ff5a1f, transparent)' }} />
                <Zap className="w-6 h-6 text-[#ff5a1f] mb-6 relative" />
                <div className="space-y-6 relative">
                  {service.metrics.map((m, i) => (
                    <div key={i} className="border-b border-white/10 pb-4 last:border-0 last:pb-0">
                      <p className="font-syne text-4xl font-bold text-[#ff5a1f] tracking-tight">{m.value}</p>
                      <p className="text-white/60 text-xs uppercase tracking-wider mt-1">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-white" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow mb-5">What's in the box</div>
            <h2 className="font-syne text-4xl md:text-5xl font-bold text-[#0A0A0A] tracking-tight">
              Capabilities, <span className="font-editorial text-[#ff5a1f]">tightly engineered.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.capabilities.map((cap, idx) => (
              <div key={idx} data-testid={`capability-${idx}`}
                className="p-7 bg-[#FAFAF7] border rounded-2xl hover:border-[#ff5a1f] transition-all duration-500 group"
                style={{ borderColor: 'var(--border)' }}>
                <div className="w-12 h-12 rounded-xl bg-white border flex items-center justify-center mb-5 group-hover:bg-[#ff5a1f] group-hover:border-[#ff5a1f] transition-colors"
                  style={{ borderColor: 'var(--border)' }}>
                  <cap.icon className="w-6 h-6 text-[#ff5a1f] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-syne text-xl font-bold text-[#0A0A0A] mb-2 tracking-tight">{cap.title}</h3>
                <p className="text-[#5B5B5B] text-sm leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="dark-section py-24 text-white relative overflow-hidden" data-testid="deliverables-section">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-25" style={{ background: 'radial-gradient(circle, #ff5a1f, transparent)' }} />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 mb-6 font-mono text-xs uppercase tracking-widest text-[#ff5a1f]">
                <span className="w-6 h-px bg-[#ff5a1f]" /> Deliverables
              </div>
              <h2 className="font-syne text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
                Everything you walk away with.
              </h2>
              <p className="text-white/70 leading-relaxed">
                No fluff, no vague handoffs. Every engagement ships tangible, usable artefacts your team can build on long after we leave.
              </p>
            </div>

            <div className="lg:col-span-7">
              <ul className="space-y-4">
                {service.deliverables.map((d, idx) => (
                  <li key={idx} className="flex items-center gap-4 p-5 bg-white/5 hover:bg-white/10 rounded-xl transition-colors border border-white/5">
                    <Sparkles className="w-5 h-5 text-[#ff5a1f] flex-shrink-0" />
                    <span className="text-white text-base">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#FAFAF7]">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-syne text-4xl md:text-6xl font-bold text-[#0A0A0A] mb-8 tracking-tight">
            Ready to start with <span className="font-editorial text-[#ff5a1f]">{service.title}</span>?
          </h2>
          <p className="text-[#5B5B5B] text-lg mb-12 max-w-2xl mx-auto">
            Tell us about your brand. We'll come back with a punchy 1-page proposal in 48 hours.
          </p>
          <Link to="/book-appointment" data-testid="service-bottom-cta" onClick={() => trackBookCall(`service_${service.slug}_bottom`)}
            className="bg-[#0A0A0A] text-white px-10 py-5 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors btn-press inline-flex items-center gap-2">
            Book a call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Other services */}
      <section className="py-20 bg-white border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <h3 className="font-syne text-2xl md:text-3xl font-bold text-[#0A0A0A] tracking-tight">Other services</h3>
            <Link to="/services" className="text-[#0A0A0A] font-semibold text-sm hover:text-[#ff5a1f] inline-flex items-center gap-2">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {otherServices.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`}
                className="group p-5 border rounded-xl bg-white hover:border-[#ff5a1f] hover:bg-[#FAFAF7] transition-all"
                style={{ borderColor: 'var(--border)' }}>
                <s.icon className="w-6 h-6 text-[#ff5a1f] mb-3" />
                <p className="font-syne font-bold text-sm text-[#0A0A0A] group-hover:text-[#ff5a1f] transition-colors">{s.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
